import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * The page heading belongs to the shell; what goes in it belongs to the page.
 *
 * ModuleShell draws the breadcrumb and the title (the active menu label) so
 * every screen starts the same way. A page adds to that heading -- a line of
 * description, its primary actions, or a more specific title such as a
 * document number -- by rendering <PageHeader>, which places those pieces
 * into slots the shell provides. The page never draws a heading of its own.
 * See docs/konvensi-ui.md.
 */

interface Slots {
  description: HTMLElement | null
  actions: HTMLElement | null
  setTitle: (title: string | undefined) => void
}

const PageHeaderContext = createContext<Slots | null>(null)

/** For the shell: the slot elements, the title override, and the provider value. */
export function usePageHeaderHost() {
  const [description, setDescription] = useState<HTMLElement | null>(null)
  const [actions, setActions] = useState<HTMLElement | null>(null)
  const [title, setTitle] = useState<string | undefined>()
  const value = useMemo<Slots>(() => ({ description, actions, setTitle }), [description, actions])
  return { value, title, descriptionRef: setDescription, actionsRef: setActions }
}

export const PageHeaderProvider = PageHeaderContext.Provider

export function PageHeader({
  title,
  description,
  actions,
}: {
  /** Replaces the menu label, e.g. with a document number on a detail page. */
  title?: string
  /** One line under the title: what this page is for, or what it is showing. */
  description?: ReactNode
  /** The page's primary actions, right-aligned beside the title. Small buttons. */
  actions?: ReactNode
}) {
  const slots = useContext(PageHeaderContext)
  const setTitle = slots?.setTitle

  useEffect(() => {
    if (!setTitle || title === undefined) return
    setTitle(title)
    // Leaving the page gives the heading back to the menu label.
    return () => setTitle(undefined)
  }, [setTitle, title])

  if (!slots) return null
  return (
    <>
      {description && slots.description && createPortal(description, slots.description)}
      {actions && slots.actions && createPortal(actions, slots.actions)}
    </>
  )
}
